/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useSpeedDialData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading Speed Dial data for edit/duplicate modes.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { getSpeedDialById } from '../api/speedDialsApi';
import type { SpeedDial } from '../api/speedDialsApi';

export interface SpeedDialFormState {
  speedCode: string;
  destination: string;
  description: string;
  classOfServicesId: string | number | null;
  enabled: boolean;
}

export const DEFAULT_SPEED_DIAL_FORM: SpeedDialFormState = {
  speedCode: '',
  destination: '',
  description: '',
  classOfServicesId: null,
  enabled: true
};

export function useSpeedDialData(id: string | undefined, duplicateId: string | null) {
  const location = useLocation();
  const { showError, showInfo } = useNotifications();
  const { t } = useI18n();

  const [formData, setFormData] = useState<SpeedDialFormState>(() => ({ ...DEFAULT_SPEED_DIAL_FORM }));
  const [baseline, setBaseline] = useState<SpeedDialFormState | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const speedDialRequestRef = useRef<AbortController | null>(null);
  const speedDialLoadedRef = useRef<string | null>(null);
  const lastLocationKeyRef = useRef<string>('');
  const showInfoRef = useRef(showInfo);
  const showErrorRef = useRef(showError);
  const translateRef = useRef(t);

  useEffect(() => {
    showInfoRef.current = showInfo;
    showErrorRef.current = showError;
    translateRef.current = t;
  }, [showInfo, showError, t]);

  useEffect(() => {
    const loadId = duplicateId || id;
    
    // Reset loaded ref when id changes
    if (speedDialLoadedRef.current !== loadId) {
      speedDialLoadedRef.current = null;
    }
    
    if (loadId && loadId !== 'new') {
      // Pass loadId directly — may be a Sqid string; server Sqids plugin decodes it
      // Prevent loading if already loaded
      if (speedDialLoadedRef.current === loadId) {
        setIsLoading(false);
        return;
      }
      
      speedDialLoadedRef.current = loadId;

      // Fetch speed dial — pass loadId directly (Sqids plugin decodes URL param server-side)
      speedDialRequestRef.current?.abort();
      const controller = new AbortController();
      speedDialRequestRef.current = controller;

      setIsLoading(true);

      getSpeedDialById(loadId)
        .then(response => {
          if (speedDialRequestRef.current !== controller) return;
          
          const payload = response.data;
          const isDuplicate = Boolean(duplicateId);
          
          const formState: SpeedDialFormState = {
            speedCode: payload.speedCode || '',
            destination: payload.destination || '',
            description: payload.description || '',
            classOfServicesId: payload.classOfServicesId ?? null,
            enabled: payload.enabled ?? true
          };
          
          if (isDuplicate) {
            // For duplicate, remove id and append _copy to speedCode
            const duplicated = {
              ...formState,
              speedCode: formState.speedCode ? `${formState.speedCode}_copy` : ''
            };
            setFormData(duplicated);
            setBaseline(null);
            showInfoRef.current?.(translateRef.current('speedDials.messages.duplicateHint', 'Speed dial duplicated. Please review and save.'));
          } else {
            setFormData(formState);
            setBaseline(formState);
          }
          setIsLoading(false);
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          if (speedDialRequestRef.current !== controller) return;
          
          const isAbort =
            error?.name === 'AbortError' ||
            (error instanceof DOMException && error.name === 'AbortError');

          if (!isAbort) {
            showErrorRef.current?.(translateRef.current('speedDials.messages.loadErrorSingle'));
          }
          setIsLoading(false);
        })
        .finally(() => {
          if (speedDialRequestRef.current === controller) {
            speedDialRequestRef.current = null;
          }
        });
    } else {
      // Check if we need to reset:
      // 1. Location key changed (React Router navigation)
      // 2. sessionStorage timestamp indicates reset needed
      const savedDataKey = 'speedDials:saveAndNewData';
      const locationKey = location.key || location.pathname + location.search;
      const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
      const lastResetTimestamp = speedDialLoadedRef.current === null ? 
        sessionStorage.getItem(savedDataKey + ':lastReset') : null;
      
      const shouldReset = lastLocationKeyRef.current !== locationKey ||
                          (speedDialLoadedRef.current === null && 
                           resetTimestamp && resetTimestamp !== lastResetTimestamp);
      
      if (shouldReset) {
        // Clear any saved data to ensure fresh form
        try {
          sessionStorage.removeItem(savedDataKey);
          if (resetTimestamp) {
            sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
            sessionStorage.removeItem(savedDataKey + ':reset');
          }
        } catch (error) {
          // Ignore errors when clearing
        }
        
        // Reset form for new speed dial
        const fresh = { ...DEFAULT_SPEED_DIAL_FORM };
        setFormData(fresh);
        setBaseline(null);
        lastLocationKeyRef.current = locationKey;
        setIsLoading(false);
      } else if (speedDialLoadedRef.current === null) {
        setIsLoading(false);
      }
    }
    
    return () => {
      speedDialRequestRef.current?.abort();
    };
  }, [id, duplicateId, location.key, location.pathname, location.search]);

  return {
    formData,
    setFormData,
    baseline,
    setBaseline,
    isLoading
  };
}

